import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Skeleton,
  Alert,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { trpc } from '../../lib/trpc';
import { AdminUsersTable } from '../../components/admin/AdminUsersTable';
import { AdminMonitorsTable } from '../../components/admin/AdminMonitorsTable';

/**
 * Tab panel component
 */
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Stat card component
 */
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
  isLoading?: boolean;
}

function StatCard({ title, value, subtitle, icon, color = 'primary.main', isLoading }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            {isLoading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={600}>
                {value}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: 1,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Admin dashboard page
 */
export function AdminDashboardPage() {
  const [tabValue, setTabValue] = useState(0);

  // Query system stats
  const { data: stats, isLoading, isError } = trpc.admin.getSystemStats.useQuery();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          System overview and management
        </Typography>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load system statistics. Please try again.
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers ?? 0}
            subtitle={`${stats?.usersByPlan.PRO ?? 0} Pro, ${stats?.usersByPlan.ENTERPRISE ?? 0} Enterprise`}
            icon={<PeopleIcon />}
            color="primary.main"
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Monitors"
            value={stats?.totalMonitors ?? 0}
            subtitle={`${stats?.activeMonitors ?? 0} active, ${stats?.pausedMonitors ?? 0} paused`}
            icon={<MonitorHeartIcon />}
            color="secondary.main"
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Checks (24h)"
            value={stats?.checksLast24h?.toLocaleString() ?? 0}
            subtitle={stats?.avgResponseTime ? `${stats.avgResponseTime}ms avg response` : 'No data'}
            icon={<CheckCircleIcon />}
            color="success.main"
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overall Uptime"
            value={`${stats?.overallUptime ?? 100}%`}
            subtitle={`${stats?.monitorsUp ?? 0} up, ${stats?.monitorsDown ?? 0} down`}
            icon={<TrendingUpIcon />}
            color={
              (stats?.overallUptime ?? 100) >= 99.5
                ? 'success.main'
                : (stats?.overallUptime ?? 100) >= 95
                ? 'warning.main'
                : 'error.main'
            }
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Admin tabs">
          <Tab
            label={`Users (${stats?.totalUsers ?? 0})`}
            id="admin-tab-0"
            aria-controls="admin-tabpanel-0"
          />
          <Tab
            label={`Monitors (${stats?.totalMonitors ?? 0})`}
            id="admin-tab-1"
            aria-controls="admin-tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <AdminUsersTable />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <AdminMonitorsTable />
      </TabPanel>
    </Box>
  );
}

export default AdminDashboardPage;
